import {
  BuildingOffice2Icon,
  CreditCardIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  PhoneIcon,
  SparklesIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import Modal from '@/components/Modal';
import Spinner from '@/components/Spinner';
import { useClientActivity, useClientBilling } from '../hooks/useClerkOps';
import { formatMoney, type OpsClient } from '../services/clerkOpsService';

const STATUS_TONE: Record<string, string> = {
  active: 'text-success',
  healthy: 'text-success',
  paid: 'text-success',
  past_due: 'text-warning',
  at_risk: 'text-warning',
  overdue: 'text-danger',
  critical: 'text-danger',
  canceled: 'text-danger',
};

function tone(v: string | null | undefined): string {
  return STATUS_TONE[(v ?? '').toLowerCase()] ?? 'text-ink';
}

function fmtDate(v: string | null): string {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? v : d.toLocaleDateString();
}

function fmtRelative(v: string | null): string {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return days < 30 ? `${days}d ago` : d.toLocaleDateString();
}

function Row({ label, value, valueTone }: { label: string; value: string; valueTone?: string }) {
  return (
    <div className="flex items-center justify-between gap-4 text-sm">
      <span className="text-ink-3">{label}</span>
      <span className={valueTone ?? 'text-ink'}>{value}</span>
    </div>
  );
}

function Section({
  icon: Icon,
  title,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <h4 className="mb-3 flex items-center gap-1.5 font-display text-sm font-semibold text-ink">
        <Icon className="h-4 w-4 text-ink-3" /> {title}
      </h4>
      {children}
    </div>
  );
}

/**
 * Full detail view for one Clerk client, opened from the roster in
 * ClerkOpsConsole. The core profile comes from the already-loaded roster row;
 * billing + activity are fetched lazily by client_id (both may 404 / return
 * empty until Clerk ships those surfaces).
 */
export default function ClientDetailModal({
  client,
  onClose,
}: {
  client: OpsClient | null;
  onClose: () => void;
}) {
  const clientId = client?.clientId ?? null;
  const { data: billing, isLoading: loadingBilling } = useClientBilling(clientId);
  const { data: activity, isLoading: loadingActivity } = useClientActivity(clientId);

  const fp = client?.featureProgress;

  return (
    <Modal
      isOpen={!!client}
      onClose={onClose}
      title={client?.name ?? client?.clientId ?? 'Client'}
      size="xl"
    >
      {!client ? null : (
        <div className="max-h-[70vh] space-y-6 overflow-y-auto pr-1">
          {/* Profile */}
          <Section icon={BuildingOffice2Icon} title="Profile">
            <div className="space-y-2 rounded-card bg-surface-2 p-3">
              <Row label="Status" value={client.status ?? '—'} valueTone={tone(client.status)} />
              <Row label="Health" value={client.health ?? '—'} valueTone={tone(client.health)} />
              <Row label="Onboarding stage" value={client.onboardingStage ?? '—'} />
              <div className="flex items-center justify-between gap-4 text-sm">
                <span className="flex items-center gap-1.5 text-ink-3">
                  <EnvelopeIcon className="h-4 w-4" /> Email
                </span>
                <span className="text-ink">{client.email ?? '—'}</span>
              </div>
              <div className="flex items-center justify-between gap-4 text-sm">
                <span className="flex items-center gap-1.5 text-ink-3">
                  <PhoneIcon className="h-4 w-4" /> Phone
                </span>
                <span className="text-ink">{client.phone ?? '—'}</span>
              </div>
              <div className="flex items-center justify-between gap-4 text-sm">
                <span className="flex items-center gap-1.5 text-ink-3">
                  <UserCircleIcon className="h-4 w-4" /> Owner
                </span>
                <span className="text-ink">{client.ownerName ?? '—'}</span>
              </div>
              <Row label="Created" value={fmtDate(client.createdAt)} />
              <Row label="Went live" value={fmtDate(client.wentLiveAt)} />
              <Row label="Last active" value={fmtRelative(client.lastActiveAt)} />
              {fp && (
                <div className="flex items-center justify-between gap-4 text-sm">
                  <span className="text-ink-3">Features used</span>
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-16 overflow-hidden rounded-full bg-surface-2">
                      <div
                        className="h-full bg-brand"
                        style={{
                          width: `${fp.total > 0 ? Math.round((fp.used / fp.total) * 100) : 0}%`,
                        }}
                      />
                    </div>
                    <span className="text-xs text-ink-3">
                      {fp.used}/{fp.total}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </Section>

          {/* Subscription / billing */}
          <Section icon={CreditCardIcon} title="Subscription">
            {loadingBilling ? (
              <div className="flex justify-center py-4">
                <Spinner />
              </div>
            ) : (
              <div className="space-y-2 rounded-card bg-surface-2 p-3">
                <Row label="Plan" value={billing?.plan ?? '—'} />
                <Row
                  label="Status"
                  value={billing?.subscriptionStatus ?? client.status ?? '—'}
                  valueTone={tone(billing?.subscriptionStatus ?? client.status)}
                />
                <Row label="Balance" value={formatMoney(billing?.balance ?? null)} />
                {billing?.nextInvoiceAt && (
                  <Row label="Next invoice" value={fmtDate(billing.nextInvoiceAt)} />
                )}
              </div>
            )}
          </Section>

          {/* Recent invoices */}
          {billing && billing.invoices.length > 0 && (
            <Section icon={DocumentTextIcon} title="Recent Invoices">
              <div className="overflow-hidden rounded-card border border-line">
                <table className="w-full text-sm">
                  <thead className="border-b border-line text-left text-ink-3">
                    <tr>
                      <th className="px-3 py-2 font-medium">Issued</th>
                      <th className="px-3 py-2 font-medium">Due</th>
                      <th className="px-3 py-2 font-medium">Amount</th>
                      <th className="px-3 py-2 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {billing.invoices.slice(0, 10).map((inv) => (
                      <tr key={inv.invoiceId} className="border-b border-line">
                        <td className="px-3 py-2 text-ink-2">{fmtDate(inv.issuedAt)}</td>
                        <td className="px-3 py-2 text-ink-3">{fmtDate(inv.dueAt)}</td>
                        <td className="px-3 py-2 text-ink">{formatMoney(inv.amount)}</td>
                        <td className={`px-3 py-2 ${tone(inv.status)}`}>{inv.status ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>
          )}

          {/* Activity */}
          <Section icon={SparklesIcon} title="Activity">
            {loadingActivity ? (
              <div className="flex justify-center py-4">
                <Spinner />
              </div>
            ) : activity && (activity.recentActions.length > 0 || activity.features.length > 0) ? (
              <div className="space-y-4">
                {activity.recentActions.length > 0 && (
                  <div className="space-y-1.5 rounded-card bg-surface-2 p-3">
                    {activity.recentActions.slice(0, 8).map((a, i) => (
                      <div
                        key={`${a.type}-${i}`}
                        className="flex items-center justify-between gap-4 text-sm"
                      >
                        <span className="text-ink-2">{a.label ?? a.type ?? '—'}</span>
                        <span className="text-ink-3">{fmtRelative(a.at)}</span>
                      </div>
                    ))}
                  </div>
                )}
                {activity.features.length > 0 && (
                  <div className="space-y-1.5 rounded-card bg-surface-2 p-3">
                    {activity.features.map((f) => (
                      <div
                        key={f.key}
                        className="flex items-center justify-between gap-4 text-sm"
                      >
                        <span className={f.status === 'used' ? 'text-ink' : 'text-ink-3'}>
                          {f.label ?? f.key}
                        </span>
                        <span className="text-ink-3">
                          {f.status === 'used' ? fmtRelative(f.lastUsedAt) : 'unused'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="rounded-card bg-surface-2 p-3 text-sm text-ink-3">
                No recent activity.
              </div>
            )}
          </Section>
        </div>
      )}
    </Modal>
  );
}
